import { ASSET_KIND_LABELS, ASSET_KINDS, type AssetKind, type MaintenanceAsset } from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import {
  Alert,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Select,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Boxes } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { warrantyState } from "../../../../features/maintenance/maintenance-values.js";
import { useMaintenanceAssets } from "../../../../features/maintenance/queries.js";
import { MaintenancePage, StationFilterField } from "../../../../features/maintenance/shared.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDate } from "../../../../lib/format.js";

const searchSchema = z.object({
  station: z.string().uuid().optional().catch(undefined),
  kind: z.enum(ASSET_KINDS).optional().catch(undefined),
  q: z.string().max(80).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/bakim/varliklar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: AssetsRoute,
});

/**
 * Varlıklar (Prompt 23 web, `GET /maintenance/assets`): tank, pompa, cihaz, şarj ve yıkama üniteleri tek listede — seri no,
 * kurulum, garanti, servis firması, açık iş emri ve etkin plan sayısı, en yakın gün planı vadesi. Satırdan varlık kartına.
 */
function AssetsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MaintenancePage title={t("menu.maintenanceAssets")} description={t("maintenance.assets.description")}>
      <AssetList />
    </MaintenancePage>
  );
}

function AssetList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [text, setText] = useState(search.q ?? "");
  const today = toIstanbulDate(new Date());
  const assets = useMaintenanceAssets({
    stationId: search.station ?? null,
    kind: search.kind ?? null,
    search: search.q ?? "",
  });
  const setSearch = (next: { station?: string | null; kind?: AssetKind | null; q?: string }) => {
    const station = next.station !== undefined ? next.station : (search.station ?? null);
    const kind = next.kind !== undefined ? next.kind : (search.kind ?? null);
    const q = next.q !== undefined ? next.q : (search.q ?? "");
    void navigate({
      to: ".",
      search: { ...(station ? { station } : {}), ...(kind ? { kind } : {}), ...(q ? { q } : {}) } as never,
      replace: true,
    });
  };

  const columns = useMemo<DataTableColumn<MaintenanceAsset>[]>(
    () => [
      {
        id: "asset",
        header: t("maintenance.fields.asset"),
        hideable: false,
        cell: (row) => (
          <span className="grid min-w-48 gap-0.5">
            <Link
              to="/bakim/varliklar/$kind/$id"
              params={{ kind: row.assetKind, id: row.assetId }}
              className="font-medium underline underline-offset-4"
            >
              {row.name}
            </Link>
            <span className="text-xs text-muted-foreground">
              {ASSET_KIND_LABELS[row.assetKind]}
              {row.serialNo ? ` · ${row.serialNo}` : ""}
            </span>
          </span>
        ),
      },
      { id: "station", header: t("maintenance.fields.station"), cell: (row) => row.stationCode },
      {
        id: "installedOn",
        header: t("maintenance.fields.installedOn"),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDate(row.installedOn)}</span>,
      },
      {
        id: "warranty",
        header: t("maintenance.fields.warrantyEndsOn"),
        cell: (row) => {
          const state = warrantyState(row.warrantyEndsOn, today);
          return state ? (
            <span className="grid justify-items-start gap-1">
              <span className="tabular whitespace-nowrap">{formatDate(row.warrantyEndsOn)}</span>
              <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
            </span>
          ) : (
            "—"
          );
        },
      },
      {
        id: "service",
        header: t("maintenance.fields.serviceCounterparty"),
        cell: (row) => row.serviceCounterparty?.name ?? "—",
      },
      {
        id: "openWorkOrders",
        header: t("maintenance.fields.openWorkOrders"),
        align: "right",
        cell: (row) =>
          row.openWorkOrders > 0 ? (
            <StatusBadge tone="warning">{formatCount(row.openWorkOrders)}</StatusBadge>
          ) : (
            <span className="tabular">0</span>
          ),
      },
      {
        id: "plans",
        header: t("maintenance.fields.activePlans"),
        align: "right",
        cell: (row) => <span className="tabular">{formatCount(row.activePlans)}</span>,
      },
      {
        id: "nextDue",
        header: t("maintenance.fields.nextPlanDueOn"),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDate(row.nextPlanDueOn)}</span>,
      },
      {
        id: "documents",
        header: t("maintenance.fields.documents"),
        align: "right",
        defaultVisible: false,
        cell: (row) => <span className="tabular">{formatCount(row.documentCount)}</span>,
      },
    ],
    [t, today],
  );

  return (
    <DataTable
      caption={t("menu.maintenanceAssets")}
      columns={columns}
      rows={assets.data?.items ?? []}
      rowKey={(row) => `${row.assetKind}:${row.assetId}`}
      loading={assets.isPending}
      error={assets.error ? <Alert tone="danger">{errorMessage(assets.error)}</Alert> : undefined}
      filters={
        <>
          <StationFilterField value={search.station ?? null} onChange={(station) => setSearch({ station })} />
          <FormField label={t("maintenance.fields.assetKind")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.kind ?? ""}
                onChange={(event) => setSearch({ kind: (event.target.value || null) as AssetKind | null })}
              >
                <option value="">{t("common.all")}</option>
                {ASSET_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {ASSET_KIND_LABELS[kind]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setSearch({ q: text.trim() });
            }}
          >
            <FormField label={t("common.search")} className="w-52">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={text}
                  placeholder={t("maintenance.assets.searchPlaceholder")}
                  onChange={(event) => setText(event.target.value)}
                />
              )}
            </FormField>
          </form>
        </>
      }
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Boxes />}
          title={t("maintenance.assets.emptyTitle")}
          description={t("maintenance.assets.emptyDescription")}
        />
      }
    />
  );
}
